import type { Property } from '../types';
import type { PropertyProgramGroup } from './propertyToProjectCard';

const uniqueStrings = (values: string[]): string[] => {
  const result: string[] = [];
  for (const value of values) {
    if (!result.includes(value)) result.push(value);
  }
  return result;
};

const uniqueEnergyClasses = (
  values: Array<Property['energyClass']>
): Array<NonNullable<Property['energyClass']>> => {
  const result: Array<NonNullable<Property['energyClass']>> = [];
  for (const value of values) {
    if (value && !result.includes(value)) result.push(value);
  }
  return result;
};

export interface ProgramServiceDistance {
  id: string;
  label: string;
  distance: string;
}

const hashProgramKey = (key: string): number =>
  key.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);

const minutesInRange = (seed: number, offset: number, min: number, max: number): number =>
  min + ((seed + offset * 17) % (max - min + 1));

/** Distances indicatives aux services (à pied) — dérivées de la clé programme pour la démo. */
export const getProgramServiceDistances = (
  programKey: string
): ProgramServiceDistance[] => {
  const seed = hashProgramKey(programKey);
  return [
    {
      id: 'shops',
      label: 'Commerces',
      distance: `${minutesInRange(seed, 1, 2, 7)} min`,
    },
    {
      id: 'schools',
      label: 'Écoles',
      distance: `${minutesInRange(seed, 2, 4, 12)} min`,
    },
    {
      id: 'transit',
      label: 'Transports',
      distance: `${minutesInRange(seed, 3, 3, 10)} min`,
    },
    {
      id: 'health',
      label: 'Santé',
      distance: `${minutesInRange(seed, 4, 5, 15)} min`,
    },
  ];
};

export interface ProgramMapKeyFacts {
  lotsCount: number;
  availableLotsCount: number;
  priceMin: number | null;
  priceMax: number | null;
  surfaceMin: number | null;
  surfaceMax: number | null;
  typologiesLabel: string | null;
  energyClass: string | null;
}

export const getProgramMapKeyFacts = (
  group: PropertyProgramGroup
): ProgramMapKeyFacts => {
  const prices = group.lots.map((lot) => lot.price).filter((p) => p > 0);
  const surfaces = group.lots.map((lot) => lot.surface).filter((s) => s > 0);
  const availableLots = group.lots.filter((lot) => lot.status === 'disponible');
  const roomLabels = uniqueStrings(
    group.lots
      .map((lot) => (lot.rooms ? `T${lot.rooms}` : null))
      .filter((label): label is string => Boolean(label))
  ).sort((a, b) => parseInt(a.slice(1), 10) - parseInt(b.slice(1), 10));

  const energyClasses = uniqueEnergyClasses(group.lots.map((lot) => lot.energyClass));

  return {
    lotsCount: group.lots.length,
    availableLotsCount: availableLots.length,
    priceMin: prices.length > 0 ? Math.min(...prices) : null,
    priceMax: prices.length > 0 ? Math.max(...prices) : null,
    surfaceMin: surfaces.length > 0 ? Math.min(...surfaces) : null,
    surfaceMax: surfaces.length > 0 ? Math.max(...surfaces) : null,
    typologiesLabel: roomLabels.length > 0 ? roomLabels.join(' · ') : null,
    energyClass: energyClasses[0] ?? null,
  };
};
